import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import {
  subscribeToChecklists,
  addChecklistSet,
  updateChecklistSet,
  deleteChecklistSet,
} from '../services/firebase';
import TripMoneyView from './TripMoneyView';
import {
  hasBudgetPlan,
  calcOverall,
  formatYen,
  getTrip,
  getSlotView,
  slotLabel,
  normalizeSlotKey,
  defaultSlotKey,
  localDateStr,
  tripTheme,
  themeLines,
} from '../utils/tripLogic';

// チェックリスト（持ち物チェックなど）
// - 旧 checklist-app（単一HTML・localStorage）を task-app に統合したもの
// - 保存先: Firestore users/{uid}/checklists（1セット＝1ドキュメント）
// - スマホ⇔PC リアルタイム同期。オフラインは Firestore の IndexedDB キャッシュに乗る
// - 並び替えは既存 task-app と同じ dnd-kit 流儀（ハンドル・長押し200ms）

// カテゴリ用アイコンのプリセット（追加・編集モーダルで選択）
const CATEGORY_ICONS = ['📦', '⭐', '💡', '🎯', '🔍', '✨', '🛍️', '🌟', '💳', '👕', '🔌', '🧴', '☂️', '🧳'];

// 一意ID生成（Firestore ドキュメント内の配列要素用）
function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// ============================================================
// チェック項目1行（dnd-kit sortable）
// ============================================================
function ChecklistItemRow({ item, onToggle, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className={`cl-item ${item.checked ? 'is-checked' : ''} ${isDragging ? 'dragging' : ''}`}>
      <button className="drag-handle" {...attributes} {...listeners} aria-label="ドラッグして並び替え">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="5" cy="3" r="1.5" />
          <circle cx="11" cy="3" r="1.5" />
          <circle cx="5" cy="8" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="13" r="1.5" />
          <circle cx="11" cy="13" r="1.5" />
        </svg>
      </button>
      <label className="task-checkbox">
        <input type="checkbox" checked={!!item.checked} onChange={onToggle} />
        <span className="checkmark" />
      </label>
      <span className="cl-item-name" onClick={onToggle}>
        {item.name}
      </span>
      <button className="task-delete-btn" onClick={onDelete} aria-label="削除">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// ============================================================
// カテゴリ並び替えモーダル内の1行（dnd-kit sortable）
// ============================================================
function SortableCategoryRow({ category }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} className={`cl-sort-row ${isDragging ? 'dragging' : ''}`}>
      <button className="drag-handle" {...attributes} {...listeners} aria-label="ドラッグして並び替え">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="5" cy="3" r="1.5" />
          <circle cx="11" cy="3" r="1.5" />
          <circle cx="5" cy="8" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="13" r="1.5" />
          <circle cx="11" cy="13" r="1.5" />
        </svg>
      </button>
      <span className="cl-sort-label">
        {category.icon} {category.name}
      </span>
    </div>
  );
}

// ============================================================
// アイコン選択行（追加・編集モーダル共用）
// ============================================================
function IconPicker({ value, onChange }) {
  return (
    <div className="cl-icon-picker" role="radiogroup" aria-label="アイコンを選択">
      {CATEGORY_ICONS.map((ic) => (
        <button
          key={ic}
          type="button"
          className={`cl-icon-choice ${value === ic ? 'active' : ''}`}
          onClick={() => onChange(ic)}
          aria-label={`アイコン ${ic}`}
        >
          {ic}
        </button>
      ))}
    </div>
  );
}

export default function ChecklistView() {
  const { user } = useAuth();
  const uid = user ? user.uid : null;

  const [sets, setSets] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [activeSetId, setActiveSetId] = useState(() => localStorage.getItem('tf_cl_activeSet') || '');
  // 折りたたみ状態（端末ローカルでよいUI状態なので localStorage）
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem('tf_cl_collapsed');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });
  // モーダル: null | {type: 'addSet'|'editSet'|'addCat'|'editCat'|'sortCats', ...}
  const [modal, setModal] = useState(null);
  const [modalText, setModalText] = useState('');
  const [modalIcon, setModalIcon] = useState(CATEGORY_ICONS[0]);
  const [modalError, setModalError] = useState('');
  const [sortDraft, setSortDraft] = useState([]);
  // カテゴリごとの追加入力欄
  const [newItemNames, setNewItemNames] = useState({});
  // 旅行費用サブビュー（💰ボタンで開く全画面ビュー）の表示状態
  const [showMoney, setShowMoney] = useState(false);
  // 旅行費用のアクティブなプラン枠（setIdごとに記憶。端末ローカルのUI状態なので localStorage。
  // プランのデータ本体は Firestore＝端末間同期。v1.10.0）
  const [tripSlotMap, setTripSlotMap] = useState(() => {
    try {
      const raw = localStorage.getItem('tf_cl_tripSlots');
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  });
  const setActiveTripSlot = useCallback((setId, slotKey) => {
    setTripSlotMap((prev) => {
      const next = { ...prev, [setId]: slotKey };
      try {
        localStorage.setItem('tf_cl_tripSlots', JSON.stringify(next));
      } catch (e) {
        /* no-op */
      }
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Firestore リアルタイム購読
  useEffect(() => {
    if (!uid) {
      setSets([]);
      setHydrated(true);
      return undefined;
    }
    const unsub = subscribeToChecklists(
      uid,
      (data) => {
        setSets(data);
        setHydrated(true);
        setLoadError(null);
      },
      (err) => {
        setHydrated(true);
        if (err && err.code === 'permission-denied') {
          setLoadError('旅行リストのアクセス権限がありません。Firebaseコンソールで firestore.rules の更新（checklists の追加）が必要です。');
        } else {
          setLoadError('旅行リストの読み込みに失敗しました。通信状態を確認してください。');
        }
      }
    );
    return () => unsub && unsub();
  }, [uid]);

  // アクティブセットの決定（存在しないIDなら先頭にフォールバック）
  const activeSet = useMemo(() => {
    if (sets.length === 0) return null;
    return sets.find((s) => s.id === activeSetId) || sets[0];
  }, [sets, activeSetId]);

  useEffect(() => {
    if (activeSet && activeSet.id !== activeSetId) {
      setActiveSetId(activeSet.id);
    }
  }, [activeSet, activeSetId]);

  useEffect(() => {
    if (activeSetId) localStorage.setItem('tf_cl_activeSet', activeSetId);
  }, [activeSetId]);

  const persistCollapsed = useCallback((next) => {
    setCollapsed(next);
    try {
      localStorage.setItem('tf_cl_collapsed', JSON.stringify(next));
    } catch (e) {
      /* no-op */
    }
  }, []);

  // ===== 書き込みヘルパー（楽観的更新＋Firestore書き込み） =====

  const writeCategories = useCallback(
    (setId, nextCategories) => {
      setSets((prev) => prev.map((s) => (s.id === setId ? { ...s, categories: nextCategories } : s)));
      if (!uid) return;
      updateChecklistSet(uid, setId, { categories: nextCategories }).catch((err) => {
        console.error('Checklist write failed:', err);
      });
    },
    [uid]
  );

  // ===== セット操作 =====

  const handleAddSet = async () => {
    const name = modalText.trim();
    if (!name) return;
    if (sets.some((s) => s.name === name)) {
      setModalError('同じ名前のセットがあります');
      return;
    }
    const maxOrder = sets.length > 0 ? Math.max(...sets.map((s) => s.order || 0)) : 0;
    closeModal();
    try {
      const newId = await addChecklistSet(uid, { name, order: maxOrder + 1, categories: [] });
      setActiveSetId(newId);
    } catch (err) {
      console.error('Add checklist set failed:', err);
    }
  };

  const handleRenameSet = () => {
    if (!activeSet) return;
    const name = modalText.trim();
    if (!name) return;
    if (name !== activeSet.name && sets.some((s) => s.name === name)) {
      setModalError('同じ名前のセットがあります');
      return;
    }
    closeModal();
    if (name === activeSet.name) return;
    setSets((prev) => prev.map((s) => (s.id === activeSet.id ? { ...s, name } : s)));
    updateChecklistSet(uid, activeSet.id, { name }).catch((err) => {
      console.error('Rename checklist set failed:', err);
    });
  };

  const handleDeleteSet = () => {
    if (!activeSet) return;
    const total = (activeSet.categories || []).reduce((n, c) => n + c.items.length, 0);
    if (!window.confirm(`「${activeSet.name}」（${total}項目）を削除しますか？この操作は元に戻せません。`)) return;
    closeModal();
    const deletedId = activeSet.id;
    setSets((prev) => prev.filter((s) => s.id !== deletedId));
    setActiveSetId('');
    deleteChecklistSet(uid, deletedId).catch((err) => {
      console.error('Delete checklist set failed:', err);
    });
  };

  // ===== カテゴリ操作 =====

  const handleAddCategory = () => {
    if (!activeSet) return;
    const name = modalText.trim();
    if (!name) return;
    const cats = activeSet.categories || [];
    if (cats.some((c) => c.name === name)) {
      setModalError('同じ名前のカテゴリがあります');
      return;
    }
    closeModal();
    writeCategories(activeSet.id, [...cats, { id: genId(), name, icon: modalIcon, items: [] }]);
  };

  const handleEditCategory = () => {
    if (!activeSet || !modal || !modal.catId) return;
    const name = modalText.trim();
    if (!name) return;
    const cats = activeSet.categories || [];
    if (cats.some((c) => c.name === name && c.id !== modal.catId)) {
      setModalError('同じ名前のカテゴリがあります');
      return;
    }
    closeModal();
    writeCategories(
      activeSet.id,
      cats.map((c) => (c.id === modal.catId ? { ...c, name, icon: modalIcon } : c))
    );
  };

  const handleDeleteCategory = (cat) => {
    if (!activeSet) return;
    if (!window.confirm(`「${cat.name}」（${cat.items.length}件）を削除しますか？この操作は元に戻せません。`)) return;
    writeCategories(activeSet.id, (activeSet.categories || []).filter((c) => c.id !== cat.id));
  };

  const commitSortCats = () => {
    if (!activeSet) return;
    const byId = new Map((activeSet.categories || []).map((c) => [c.id, c]));
    const next = sortDraft.map((id) => byId.get(id)).filter(Boolean);
    closeModal();
    if (next.length === (activeSet.categories || []).length) {
      writeCategories(activeSet.id, next);
    }
  };

  const handleSortCatsDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSortDraft((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const toggleCollapse = (catId) => {
    if (!activeSet) return;
    const key = `${activeSet.id}:${catId}`;
    persistCollapsed({ ...collapsed, [key]: !collapsed[key] });
  };

  // ===== アイテム操作 =====

  const toggleItem = (catId, itemId) => {
    if (!activeSet) return;
    writeCategories(
      activeSet.id,
      (activeSet.categories || []).map((c) =>
        c.id === catId
          ? { ...c, items: c.items.map((it) => (it.id === itemId ? { ...it, checked: !it.checked } : it)) }
          : c
      )
    );
  };

  const deleteItem = (catId, itemId) => {
    if (!activeSet) return;
    writeCategories(
      activeSet.id,
      (activeSet.categories || []).map((c) =>
        c.id === catId ? { ...c, items: c.items.filter((it) => it.id !== itemId) } : c
      )
    );
  };

  const addItem = (catId) => {
    if (!activeSet) return;
    const name = (newItemNames[catId] || '').trim();
    if (!name) return;
    setNewItemNames((prev) => ({ ...prev, [catId]: '' }));
    writeCategories(
      activeSet.id,
      (activeSet.categories || []).map((c) =>
        c.id === catId ? { ...c, items: [...c.items, { id: genId(), name, checked: false }] } : c
      )
    );
  };

  const handleItemDragEnd = (catId) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !activeSet) return;
    const cat = (activeSet.categories || []).find((c) => c.id === catId);
    if (!cat) return;
    const oldIndex = cat.items.findIndex((it) => it.id === active.id);
    const newIndex = cat.items.findIndex((it) => it.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    writeCategories(
      activeSet.id,
      (activeSet.categories || []).map((c) =>
        c.id === catId ? { ...c, items: arrayMove(c.items, oldIndex, newIndex) } : c
      )
    );
  };

  // ===== 全チェック／リセット =====

  const setAllChecked = (value) => {
    if (!activeSet) return;
    if (!value && !window.confirm('このセットのチェックをすべてリセットしますか？')) return;
    writeCategories(
      activeSet.id,
      (activeSet.categories || []).map((c) => ({
        ...c,
        items: c.items.map((it) => ({ ...it, checked: value })),
      }))
    );
  };

  // ===== モーダル共通 =====
  // （旧アプリからのインポート機能は v1.9.2 で削除。社長のデータ移行完了済みのため）

  const openModal = (m) => {
    setModal(m);
    setModalError('');
    if (m.type === 'editSet' && activeSet) setModalText(activeSet.name);
    else if (m.type === 'editCat' && activeSet) {
      const cat = (activeSet.categories || []).find((c) => c.id === m.catId);
      setModalText(cat ? cat.name : '');
      setModalIcon(cat && cat.icon ? cat.icon : CATEGORY_ICONS[0]);
    } else if (m.type === 'addCat') {
      setModalText('');
      const used = ((activeSet && activeSet.categories) || []).length;
      setModalIcon(CATEGORY_ICONS[used % CATEGORY_ICONS.length]);
    } else if (m.type === 'sortCats' && activeSet) {
      setSortDraft((activeSet.categories || []).map((c) => c.id));
      setModalText('');
    } else {
      setModalText('');
    }
  };

  const closeModal = () => {
    setModal(null);
    setModalText('');
    setModalError('');
  };

  // ===== 進捗 =====

  const progress = useMemo(() => {
    if (!activeSet) return { done: 0, total: 0, pct: 0 };
    let done = 0;
    let total = 0;
    (activeSet.categories || []).forEach((c) => {
      c.items.forEach((it) => {
        total += 1;
        if (it.checked) done += 1;
      });
    });
    return { done, total, pct: total > 0 ? (done / total) * 100 : 0 };
  }, [activeSet]);

  const allDone = progress.total > 0 && progress.done === progress.total;

  // セットが消えた・切り替わった場合に費用ビューを閉じる事故を防ぐ
  // （activeSet が無くなったら費用ビューは表示できない）
  useEffect(() => {
    if (!activeSet && showMoney) setShowMoney(false);
  }, [activeSet, showMoney]);

  // 選択中のプラン枠（不正値は枠1に落とす）と、その枠のデータビュー（v1.10.0）
  // 一度も選んだことのないセットは、旅行日が近いプランを既定にする（v1.15.0）
  const activeSlot = activeSet
    ? tripSlotMap[activeSet.id]
      ? normalizeSlotKey(tripSlotMap[activeSet.id])
      : defaultSlotKey(activeSet, localDateStr())
    : 'slot1';
  const moneyView = activeSet ? getSlotView(activeSet, activeSlot) : null;

  // ===== 旅行費用サブビュー（全画面切替） =====
  if (showMoney && activeSet) {
    return (
      <div className="task-column column-teal checklist-view">
        <TripMoneyView
          uid={uid}
          set={activeSet}
          slotKey={activeSlot}
          onSlotChange={(k) => setActiveTripSlot(activeSet.id, normalizeSlotKey(k))}
          onBack={() => setShowMoney(false)}
        />
      </div>
    );
  }

  // ===== 描画 =====

  const modalTitleMap = {
    addSet: '➕ セットを追加',
    editSet: '✏️ セットの編集',
    addCat: '➕ カテゴリを追加',
    editCat: '✏️ カテゴリの編集',
    sortCats: '🔀 カテゴリの順番を変える',
  };

  return (
    <div className="task-column column-teal checklist-view">
      <div className="column-header">
        <div className="column-title-row">
          <span className="column-icon">🧳</span>
          <h2 className="column-title">旅行リスト</h2>
        </div>
        {progress.total > 0 && (
          <span className="column-count">
            {progress.done}/{progress.total}
          </span>
        )}
      </div>

      {loadError && <div className="cl-error-note">{loadError}</div>}

      {/* セット切り替えチップ */}
      <div className="cl-set-tabs">
        {sets.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`cl-set-tab ${activeSet && s.id === activeSet.id ? 'active' : ''}`}
            onClick={() => setActiveSetId(s.id)}
          >
            {s.name}
          </button>
        ))}
        {activeSet && (
          <button
            type="button"
            className="cl-set-tool-btn"
            onClick={() => openModal({ type: 'editSet' })}
            aria-label="セットの編集"
            title="セット名の変更・削除"
          >
            ✏️
          </button>
        )}
        <button
          type="button"
          className="cl-set-tool-btn"
          onClick={() => openModal({ type: 'addSet' })}
          aria-label="セットを追加"
          title="セットを追加"
        >
          ＋
        </button>
      </div>

      {/* 💰 旅行費用ボタン（セット切替チップの直下）。表示は選択中プラン枠のサマリー
          メインテーマ設定済みなら1行目を挟む（旅行の方向性が一覧でも目に入るように・v1.11.0） */}
      {activeSet && moneyView && (
        <button type="button" className="tm-open-btn" onClick={() => setShowMoney(true)}>
          {(() => {
            const mainLines = themeLines(tripTheme(moneyView, 'mainTheme'));
            const themeLine =
              mainLines.length > 0 ? (
                <span className="tm-open-theme">
                  🎯 {mainLines[0]}
                  {mainLines.length > 1 ? `（+${mainLines.length - 1}）` : ''}
                </span>
              ) : null;
            if (hasBudgetPlan(moneyView)) {
              const o = calcOverall(moneyView);
              const trip = getTrip(moneyView);
              const kind = trip && trip.kind === 'business' ? 'business' : 'private';
              return (
                <span className="tm-open-inner">
                  <span className="tm-open-title">
                    💰 旅行費用{' '}
                    <span className="tm-open-plan">{slotLabel(activeSet, activeSlot)}</span>{' '}
                    <span className={`tm-kind-badge tm-kind-${kind}`}>
                      {kind === 'business' ? '事業' : 'private'}
                    </span>
                  </span>
                  {themeLine}
                  <span className="tm-open-summary">
                    予算 {formatYen(o.budgetTotal)}・実費 {formatYen(o.actualTotal)}
                    {o.remaining >= 0 ? `・のこり ${formatYen(o.remaining)}` : (
                      <span className="tm-open-over">・{formatYen(Math.abs(o.remaining))} オーバー</span>
                    )}
                  </span>
                  <span className="tm-open-arrow" aria-hidden="true">＞</span>
                </span>
              );
            }
            return (
              <span className="tm-open-inner">
                <span className="tm-open-title">
                  💰 予算を決める <span className="tm-open-plan">{slotLabel(activeSet, activeSlot)}</span>
                </span>
                {themeLine}
                <span className="tm-open-summary">旅行の予算と実費を記録できます（プランは＋でいくつでも追加可）</span>
                <span className="tm-open-arrow" aria-hidden="true">＞</span>
              </span>
            );
          })()}
        </button>
      )}

      {/* 進捗バー（チェックリスト由来の大きめ表示） */}
      {activeSet && progress.total > 0 && (
        <div className="cl-progress-wrap">
          <div className="cl-progress">
            <div className="cl-progress-bar" style={{ width: `${progress.pct}%` }} />
          </div>
          <span className="cl-progress-text">
            {progress.done} / {progress.total}
          </span>
        </div>
      )}

      {/* 準備完了メッセージ */}
      {allDone && (
        <div className="cl-complete-msg">
          <span className="cl-complete-emoji">🎉</span>
          <div>
            <p className="cl-complete-title">準備完了！</p>
            <p className="cl-complete-sub">忘れ物なし、いってらっしゃい！</p>
          </div>
        </div>
      )}

      <div className="cl-body">
        {!hydrated && <p className="cl-loading">読み込み中...</p>}

        {hydrated && sets.length === 0 && !loadError && (
          <div className="empty-state cl-empty">
            <span className="empty-icon">🧳</span>
            <p>旅行リストはまだありません</p>
            <div className="cl-empty-actions">
              <button type="button" className="btn btn-accent" onClick={() => openModal({ type: 'addSet' })}>
                ＋ セットを追加
              </button>
            </div>
          </div>
        )}

        {activeSet &&
          (activeSet.categories || []).map((cat) => {
            const colKey = `${activeSet.id}:${cat.id}`;
            const isCollapsed = !!collapsed[colKey];
            const done = cat.items.filter((it) => it.checked).length;
            const catAllDone = cat.items.length > 0 && done === cat.items.length;
            return (
              <div key={cat.id} className="cl-category">
                <div className="cl-cat-head">
                  <button type="button" className="cl-cat-title" onClick={() => toggleCollapse(cat.id)}>
                    <span className="cl-cat-icon">{cat.icon}</span>
                    <span className="cl-cat-name">{cat.name}</span>
                    <span className="cl-cat-cnt">({cat.items.length})</span>
                  </button>
                  <div className="cl-cat-right">
                    <span className={`cl-cat-badge ${catAllDone ? 'done' : ''}`}>
                      {done}/{cat.items.length}
                    </span>
                    <button
                      type="button"
                      className="cl-cat-btn"
                      onClick={() => openModal({ type: 'editCat', catId: cat.id })}
                      aria-label={`${cat.name}を編集`}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="cl-cat-btn cl-cat-btn-danger"
                      onClick={() => handleDeleteCategory(cat)}
                      aria-label={`${cat.name}を削除`}
                    >
                      ×
                    </button>
                    <button
                      type="button"
                      className={`cl-chevron ${isCollapsed ? 'collapsed' : ''}`}
                      onClick={() => toggleCollapse(cat.id)}
                      aria-label={isCollapsed ? '開く' : '折りたたむ'}
                    >
                      ▼
                    </button>
                  </div>
                </div>

                {!isCollapsed && (
                  <div className="cl-cat-items">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleItemDragEnd(cat.id)}
                      modifiers={[({ transform }) => ({ ...transform, x: 0 })]}
                    >
                      <SortableContext items={cat.items.map((it) => it.id)} strategy={verticalListSortingStrategy}>
                        {cat.items.map((it) => (
                          <ChecklistItemRow
                            key={it.id}
                            item={it}
                            onToggle={() => toggleItem(cat.id, it.id)}
                            onDelete={() => deleteItem(cat.id, it.id)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>

                    <form
                      className="task-add-form cl-add-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        addItem(cat.id);
                      }}
                    >
                      <input
                        type="text"
                        className="task-add-input"
                        placeholder="追加..."
                        value={newItemNames[cat.id] || ''}
                        onChange={(e) => setNewItemNames((prev) => ({ ...prev, [cat.id]: e.target.value }))}
                        maxLength={120}
                      />
                      <button type="submit" className="task-add-btn" disabled={!(newItemNames[cat.id] || '').trim()} aria-label="追加">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="12" y1="5" x2="12" y2="19" />
                          <line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                      </button>
                    </form>
                  </div>
                )}
              </div>
            );
          })}

        {activeSet && (
          <div className="cl-footer-actions">
            {(activeSet.categories || []).length > 1 && (
              <button type="button" className="recur-open-btn" onClick={() => openModal({ type: 'sortCats' })}>
                <span className="recur-open-icon" aria-hidden="true">🔀</span>
                カテゴリの順番を変える
              </button>
            )}
            <button type="button" className="cl-add-cat-btn" onClick={() => openModal({ type: 'addCat' })}>
              ＋ カテゴリを追加
            </button>
          </div>
        )}
      </div>

      {/* 全チェック／リセット（固定バー。チェックリストページ表示中のみ） */}
      {activeSet && progress.total > 0 && (
        <div className="finish-day-bar cl-action-bar">
          <button type="button" className="cl-bar-btn cl-bar-reset" onClick={() => setAllChecked(false)}>
            🔄 リセット
          </button>
          <button type="button" className="cl-bar-btn cl-bar-checkall" onClick={() => setAllChecked(true)}>
            ✔️ 全チェック
          </button>
        </div>
      )}

      {/* ===== モーダル ===== */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalTitleMap[modal.type]}</h2>
              <button className="modal-close" onClick={closeModal} aria-label="閉じる">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {(modal.type === 'addSet' || modal.type === 'editSet') && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (modal.type === 'addSet') handleAddSet();
                    else handleRenameSet();
                  }}
                >
                  <input
                    type="text"
                    className="cl-modal-input"
                    placeholder="セット名（例：キャンプ）"
                    value={modalText}
                    onChange={(e) => setModalText(e.target.value)}
                    maxLength={30}
                    autoFocus
                  />
                  {modalError && <p className="error-text">{modalError}</p>}
                  <div className="modal-actions">
                    {modal.type === 'editSet' && (
                      <button type="button" className="btn cl-btn-danger" onClick={handleDeleteSet}>
                        🗑 削除
                      </button>
                    )}
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      キャンセル
                    </button>
                    <button type="submit" className="btn btn-accent" disabled={!modalText.trim()}>
                      {modal.type === 'addSet' ? '追加' : '変更'}
                    </button>
                  </div>
                </form>
              )}

              {(modal.type === 'addCat' || modal.type === 'editCat') && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (modal.type === 'addCat') handleAddCategory();
                    else handleEditCategory();
                  }}
                >
                  <input
                    type="text"
                    className="cl-modal-input"
                    placeholder="カテゴリ名（例：貴重品）"
                    value={modalText}
                    onChange={(e) => setModalText(e.target.value)}
                    maxLength={30}
                    autoFocus
                  />
                  <IconPicker value={modalIcon} onChange={setModalIcon} />
                  {modalError && <p className="error-text">{modalError}</p>}
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      キャンセル
                    </button>
                    <button type="submit" className="btn btn-accent" disabled={!modalText.trim()}>
                      {modal.type === 'addCat' ? '追加' : '変更'}
                    </button>
                  </div>
                </form>
              )}

              {modal.type === 'sortCats' && activeSet && (
                <div>
                  <p className="cl-modal-hint">ハンドル（⋮⋮）をドラッグして並び替え</p>
                  <div className="cl-sort-list">
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleSortCatsDragEnd}
                      modifiers={[({ transform }) => ({ ...transform, x: 0 })]}
                    >
                      <SortableContext items={sortDraft} strategy={verticalListSortingStrategy}>
                        {sortDraft
                          .map((id) => (activeSet.categories || []).find((c) => c.id === id))
                          .filter(Boolean)
                          .map((cat) => (
                            <SortableCategoryRow key={cat.id} category={cat} />
                          ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                      キャンセル
                    </button>
                    <button type="button" className="btn btn-accent" onClick={commitSortCats}>
                      ✔ 完了
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
